// Query key factory for TanStack Query
// Provides type-safe, consistent query keys across the application

export const queryKeys = {
  // User/Account keys
  user: ['user'] as const,
  accounts: {
    all: ['accounts'] as const,
    detail: (accountName: string) => ['accounts', accountName] as const,
    status: (accountName: string) =>
      ['accounts', accountName, 'status'] as const,
    nextFreeTime: (accountName: string) =>
      ['accounts', accountName, 'next-free-time'] as const,
  },

  // Rental keys
  rentals: {
    all: ['rentals'] as const,
    current: (accountName: string) =>
      ['rentals', accountName, 'current'] as const,
    fuelCard: (accountName: string) =>
      ['rentals', accountName, 'fuel-card'] as const,
  },

  // Trip history keys
  trips: {
    all: ['trips'] as const,
    history: (accountName: string, page: number) =>
      ['trips', accountName, 'history', page] as const,
  },

  // Search keys
  searches: {
    all: ['searches'] as const,
    current: (accountName: string) =>
      ['searches', accountName, 'current'] as const,
    filters: () => ['searches', 'filters'] as const,
    loopIntervals: () => ['searches', 'loop-intervals'] as const,
    optimization: (accountName: string) =>
      ['searches', accountName, 'optimization'] as const,
  },

  // Vehicle keys
  vehicles: {
    all: ['vehicles'] as const,
    detail: (vehicleId: number) => ['vehicles', vehicleId] as const,
  },

  // Cache keys
  cache: {
    status: ['cache', 'status'] as const,
    config: ['cache', 'config'] as const,
  },

  // Accessories
  accessories: ['accessories'] as const,

  // Vehicle models
  vehicleModels: ['vehicle-models'] as const,

  // Version
  version: ['version'] as const,

  // Zones
  zones: {
    all: ['zones'] as const,
  },

  // Webhooks
  webhooks: {
    all: ['webhooks'] as const,
    list: (skip?: number, limit?: number) =>
      ['webhooks', 'list', { skip, limit }] as const,
    detail: (webhookId: string) => ['webhooks', webhookId] as const,
    deliveries: (webhookId: string, skip?: number, limit?: number) =>
      ['webhooks', webhookId, 'deliveries', { skip, limit }] as const,
    delivery: (deliveryId: string) =>
      ['webhooks', 'delivery', deliveryId] as const,
    events: ['webhooks', 'events'] as const,
  },

  // Account policies
  policies: {
    all: ['policies'] as const,
    actions: ['policies', 'actions'] as const,
  },

  // Subscriptions
  subscriptions: {
    all: ['subscriptions'] as const,
  },

  // Audit logs
  auditLogs: {
    all: ['audit-logs'] as const,
    list: (filters?: object) => ['audit-logs', 'list', filters] as const,
    detail: (logId: number) => ['audit-logs', logId] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    searchStats: (days?: number, accountName?: string) =>
      ['analytics', 'search-stats', { days, accountName }] as const,
    vehicleStats: (days?: number, accountName?: string) =>
      ['analytics', 'vehicle-stats', { days, accountName }] as const,
  },

  // Favorite Locations
  favoriteLocations: {
    all: ['favorite-locations'] as const,
    list: () => ['favorite-locations', 'list'] as const,
  },

  // Quotas
  quotas: {
    all: ['quotas'] as const,
    me: ['quotas', 'me'] as const,
    account: (accountName: string) => ['quotas', accountName] as const,
  },
  // Snow Hunt
  snowHunt: {
    all: ['snow-hunt'] as const,
    current: (primaryAccount: string) =>
      ['snow-hunt', primaryAccount, 'current'] as const,
  },

  // Dispatcher
  dispatcher: {
    agents: ['dispatcher', 'agents'] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    preferences: () => ['notifications', 'preferences'] as const,
    eventTypes: () => ['notifications', 'event-types'] as const,
  },
} as const
